/**
 * The words a case card says about its court and its age (proposal 5,
 * kanban 59162c19): "Their court · Jake Butchko · asked 2d ago · reminder
 * Mon, Oct 12", "Our court", "Ready to close", and "Opened Sep 30 · 9 days".
 * The court itself comes from the server (DoorInvestigationRead.court), so
 * this only phrases it.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorCaseCourt, DoorInvestigationRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';

const DAY_MS = 86_400_000;

function wholeDaysBetween(from: string, now: Date): number | null {
  const t = new Date(from).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

function weekdayDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "asked today" / "asked 3d ago"; null without a date. */
export function askedAgo(askedAt: string | null, now: Date = new Date()): string | null {
  if (!askedAt) return null;
  const days = wholeDaysBetween(askedAt, now);
  if (days === null) return null;
  return days === 0 ? 'asked today' : `asked ${days}d ago`;
}

function shortDate(iso: string): string {
  // A bare date (YYYY-MM-DD) is a calendar day, not an instant: read it as
  // local noon so it never slips to the day before in a US timezone.
  const d = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** True when a court comes from the case's wait (not an ask). */
export function courtFromWait(court: DoorCaseCourt): boolean {
  return court.side === 'theirs' && (court.basis === 'told' || court.basis === 'judged');
}

/** "expected Oct 28" / "expected Oct 28 · overdue" for a wait with a date. */
export function expectedWord(court: DoorCaseCourt, now: Date = new Date()): string | null {
  if (!court.expected_on) return null;
  const due = new Date(`${court.expected_on}T23:59:59`);
  const overdue = !Number.isNaN(due.getTime()) && due.getTime() < now.getTime();
  return `expected ${shortDate(court.expected_on)}${overdue ? ' · overdue' : ''}`;
}

/** The card's court line. From a wait (kanban 88f93c9b): "Their court ·
 *  Hartz · since Oct 14 · expected Oct 28"; from an ask: "Their court ·
 *  Jake Butchko · asked 2d ago · reminder Mon, Oct 12". */
export function courtLine(court: DoorCaseCourt, now: Date = new Date()): string {
  if (court.side === 'ours') return 'Our court';
  if (court.side === 'ready') return 'Ready to close';
  if (court.side === 'watching') return watchingLine(court);
  if (court.side === 'done') return 'Closed';
  const parts = ['Their court'];
  if (court.person) parts.push(court.person);
  if (courtFromWait(court)) {
    if (court.since) parts.push(`since ${shortDate(court.since)}`);
    const expected = expectedWord(court, now);
    if (expected) parts.push(expected);
    return parts.join(' · ');
  }
  const asked = askedAgo(court.asked_at, now);
  if (asked) parts.push(asked);
  if (court.next_reminder_at) parts.push(`reminder ${weekdayDate(court.next_reminder_at)}`);
  return parts.join(' · ');
}

/** "Waiting on effectiveness · watch ends Apr 30" (kanban d91f705b): fixed
 *  on our end, watching for a repeat on the same cause until the Sigma
 *  stage-3 window runs out. `expected_on` is the day the watch ends. */
export function watchingLine(court: Pick<DoorCaseCourt, 'expected_on'>): string {
  const parts = ['Waiting on effectiveness'];
  if (court.expected_on) parts.push(`watch ends ${shortDate(court.expected_on)}`);
  return parts.join(' · ');
}

/** The one line under the court on a card: the wait's reason, with who
 *  said so ("Root cause waits on Hartz's samples; Rich asked 10/14 — Merlin
 *  judged" / "— Jess said"). Null when the court is not from a wait. */
export function courtReasonLine(court: DoorCaseCourt): string | null {
  if (!courtFromWait(court)) return null;
  const reason = (court.reason ?? '').trim();
  if (!reason) return null;
  const who = court.basis === 'judged' ? 'Merlin judged' : 'a person said so';
  return `${reason} — ${who}`;
}

function openOn(inv: Pick<DoorInvestigationRead, 'tasks'>, owner: 'customer' | 'colleague'): number {
  return (inv.tasks ?? []).filter((t) => t.status === 'open' && t.owner === owner).length;
}

/**
 * "2 open items wait on a colleague, and nobody has been asked" — said
 * under "Our court" (UX round 2026-10-13). The court follows the asks
 * (proposal 5), so STATE01 and the Hartz case read "Our court" for weeks
 * while their open items waited on someone else, and the reader took the
 * board to be wrong. In our court no ask is open, so an open item on a
 * colleague's desk has not been asked about. The customer's items are not
 * counted: nothing is meant to be asked for them (customerItemsLine). Null
 * on any other court, or when no open item is a colleague's.
 */
export function unaskedItemsLine(
  inv: Pick<DoorInvestigationRead, 'court' | 'tasks'>,
): string | null {
  if (inv.court.side !== 'ours') return null;
  const colleague = openOn(inv, 'colleague');
  if (colleague === 0) return null;
  const items = colleague === 1 ? '1 open item waits' : `${colleague} open items wait`;
  return `${items} on a colleague, and nobody has been asked`;
}

/**
 * "1 open item waits on the customer" — the customer's open items while
 * the case is in our court (kanban d3915b86). Quality reaches the customer
 * through the account's sales rep, never with an ask from here, so the
 * court line points at the item's recommendation instead of an Ask button.
 */
export function customerItemsLine(
  inv: Pick<DoorInvestigationRead, 'court' | 'tasks'>,
): string | null {
  if (inv.court.side !== 'ours') return null;
  const customer = openOn(inv, 'customer');
  if (customer === 0) return null;
  return customer === 1 ? '1 open item waits on the customer' : `${customer} open items wait on the customer`;
}

/** "Opened Sep 30 · 9 days" ("· today" on the day, "· 1 day" the next). */
export function openedLine(
  inv: Pick<DoorInvestigationRead, 'created_at'>,
  now: Date = new Date(),
): string {
  const opened = new Date(inv.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const days = wholeDaysBetween(inv.created_at, now) ?? 0;
  const age = days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
  return `Opened ${opened} · ${age}`;
}

/** The "Waiting on people" row's word, from what the request carries:
 *  "answered · unread" (answer_unseen) or "✓ sent" (sent_at); null when
 *  neither is known. Delivery per channel is not on the request. */
export function askSentWord(
  r: Pick<DoorDataRequestRead, 'status' | 'sent_at' | 'answer_unseen'>,
): { key: 'sent' | 'unread'; text: string } | null {
  if (r.status === 'answered' && r.answer_unseen) return { key: 'unread', text: 'answered · unread' };
  if (r.status === 'open' && r.sent_at) return { key: 'sent', text: '✓ sent' };
  return null;
}

/* ---------------------------------------------------------------- */
/* The rail's "Whose court" filter                                  */
/* ---------------------------------------------------------------- */

/** Which column the "Whose court" filter keeps ('any' keeps all). */
export type CourtFilter = 'any' | 'ours' | 'theirs' | 'ready' | 'watching';

export const COURT_CHIPS: ReadonlyArray<{ key: CourtFilter; label: string }> = [
  { key: 'any', label: 'Any court' },
  { key: 'ours', label: 'Our court' },
  { key: 'theirs', label: 'Their court' },
  { key: 'ready', label: 'Ready to close' },
  { key: 'watching', label: 'Waiting on effectiveness' },
];

/** Narrow to one court ('any' passes everything through). */
export function applyCourtFilter<T extends Pick<DoorInvestigationRead, 'court'>>(
  investigations: T[],
  court: CourtFilter,
): T[] {
  if (court === 'any') return investigations;
  return investigations.filter((i) => i.court.side === court);
}
